'use client'

import { type AxiosError } from 'axios'
import { type FC, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { type SWRResponse } from 'swr'
import { useSessionStorage } from 'usehooks-ts'

import { type CartMiniCartV4 } from '@redteclab/api/clients/bully'
import { type CrossSellResponse } from '@redteclab/api/clients/retail-media-xsell-service'

import { Button, Dialog, DialogContent, DialogTitle } from 'base-ui'

import { useOpenableControls } from '../../../generic-hooks'
import { gtmDataLayerPushCswProduct } from '../../../gtm'
import { useUserApiSwrGetMiniCart } from '../../../user-data'
import { useSwrCrossSellHorizontalSlider } from '../../api/swr/useSwrCrossSellHorizontalSlider'
import { CROSS_SELL_LAYOUT_TYPE } from '../../config/CrossSellWidgetType'
import { useCrossSellConfigContext } from '../CrossSellConfigProvider'
import { CrossSellHorizontalSlider } from '../CrossSellHorizontalSlider'
import { CrossSellMetadataProvider } from '../CrossSellMetatadaProvider'
import { CrossSellProductCard } from '../CrossSellProductCard'
import { CrossSellWidgetContainer } from '../CrossSellWidgetContainer'

const CROSS_SELL_LAYOUT_POPUP_SHOW_TIMEOUT = 150_000
const CROSS_SELL_LAYOUT_PRODUCTS_COUNT = 5

const useCrossSellDialogPopupControls = (): {
  handleClose: () => void
  isOpen: boolean
} => {
  const miniCartSwr: SWRResponse<CartMiniCartV4> = useUserApiSwrGetMiniCart()

  const modalControls = useOpenableControls(false)

  const [crossSellLayoutShown, setCrossSellLayoutShown] = useSessionStorage(
    'crossSellLayoutShown',
    0,
    {
      /**
       * `CrossSellDialog` is wrapped with `dynamic` and rendered only on
       * the client side.
       */
      initializeWithValue: true,
    },
  )

  /** close popup on any navigation */

  const { data, isLoading } = miniCartSwr
  const { entriesInCart } = data ?? {}

  useEffect(() => {
    /** wait till mini cart is loaded, beacause logic relies on item count in cart */
    if (isLoading) {
      return undefined
    }
    /** if previously shown or non 0 items in cart -- skip */
    if (entriesInCart) {
      return undefined
    }

    const showPopup = setTimeout(() => {
      if (!crossSellLayoutShown) {
        setCrossSellLayoutShown(1)
        modalControls.handleOpen()
      }
    }, CROSS_SELL_LAYOUT_POPUP_SHOW_TIMEOUT)

    return (): void => {
      clearTimeout(showPopup)
    }
  }, [
    crossSellLayoutShown,
    entriesInCart,
    isLoading,
    modalControls,
    setCrossSellLayoutShown,
  ])

  return modalControls
}

const useCrossSellLayoutWidgetControls = ({
  fetchingEnabled,
  widgetId,
}: {
  fetchingEnabled: boolean
  widgetId?: number
}): {
  data?: CrossSellResponse
  error?: AxiosError
} => {
  const { data, error } = useSwrCrossSellHorizontalSlider(
    fetchingEnabled
      ? {
          visibleItemCount: 5,
          widgetId,
        }
      : undefined,
  )

  return {
    data,
    error,
  }
}

export const CrossSellDialog: FC = () => {
  const popupDialogControls = useCrossSellDialogPopupControls()
  const { widgets } = useCrossSellConfigContext()
  const widgetId = widgets[CROSS_SELL_LAYOUT_TYPE]?.id
  const widgetTitle = widgets[CROSS_SELL_LAYOUT_TYPE]?.title
  const widgetControls = useCrossSellLayoutWidgetControls({
    fetchingEnabled: popupDialogControls.isOpen,
    widgetId,
  })

  const { formatMessage } = useIntl()
  const { data } = widgetControls

  if (!data) {
    return null
  }

  return (
    <Dialog
      onClose={popupDialogControls.handleClose}
      open={popupDialogControls.isOpen}
      slotProps={{
        dialog: {
          className: 'max-w-5xl',
        },
      }}
    >
      <DialogTitle onClose={popupDialogControls.handleClose}>
        <div className="block text-center">
          {widgetTitle}
          <br />
          <span className="text-m font-normal">
            <FormattedMessage id="csw.popUp.do.you.need.inspiration.subtitle" />
          </span>
        </div>
      </DialogTitle>
      <DialogContent>
        <CrossSellMetadataProvider metadata={data.metadata}>
          <CrossSellWidgetContainer
            widgetData={data}
            widgetId={widgetId}
            widgetType={CROSS_SELL_LAYOUT_TYPE}
          >
            <CrossSellHorizontalSlider
              onGtmDataLayerPush={(cswProductId) => {
                gtmDataLayerPushCswProduct({
                  cswProductId,
                  widgetId,
                  widgetTitle,
                })
              }}
              products={data.items}
              productsPerPage={CROSS_SELL_LAYOUT_PRODUCTS_COUNT}
              slots={{
                itemContent: CrossSellProductCard,
              }}
            />
          </CrossSellWidgetContainer>
        </CrossSellMetadataProvider>
        <div className="pt-2 text-center">
          <Button onClick={popupDialogControls.handleClose} variant="outlined">
            {formatMessage({ id: 'csw.popUp.closeAndGoBack' })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
